import { useCallback, useEffect } from "react";
import * as THREE from "three";
import { addHighlightOverlay, removeHighlightOverlay } from "../highlightUtils";
import { findGeomNode } from "../../utils/geomSelection";
import { useSelectionStore } from "../../stores/selectionStore";
import { EMPTY_BODY_SELECTION_HIGHLIGHT_RADIUS } from "../scene/sceneConstants";
import {
  addJointSelectionHighlight,
  removeJointSelectionHighlights,
} from "../scene/jointSelectionHighlight";
import { safeTraverse } from "../scene/sceneObjectLookups";

export function useSelectionHighlightOverlays({
  bodies,
  highlightedBody,
  highlightedJoint,
  highlightedMesh,
  hoverPreviewBody,
  hoverPreviewJoint,
  hoverPreviewMesh,
  sceneObjects,
  sceneRef,
  selectedBodiesMultiKey,
  selectedGeom,
  selectedJoint,
  selectedVisualMeshIdsForScene,
  selectedVisualMeshMultiKey,
  selectionHighlightUi,
  showVisual,
}) {
  const addHighlightOverlayToBodyObject = (bodyObj, targetBodyUuid) => {
    if (!bodyObj || !targetBodyUuid || typeof bodyObj.traverse !== "function") return;
    let highlightedVisual = false;
    let bodyLabelMarker = null;

    const addBodyLabelMarkerHighlightOverlay = (markerMesh) => {
      if (!markerMesh?.isMesh) return;
      removeHighlightOverlay(markerMesh);
      const overlay = new THREE.Mesh(
        new THREE.SphereGeometry(EMPTY_BODY_SELECTION_HIGHLIGHT_RADIUS, 24, 18),
        new THREE.MeshBasicMaterial({
          color: 0x4893f4,
          transparent: true,
          opacity: 0.24,
          depthTest: false,
          depthWrite: false,
        })
      );
      overlay.name = "__bodyLabelMarkerSelectionHighlight";
      overlay.userData.isHighlightOverlay = true;
      overlay.renderOrder = 10002;
      markerMesh.add(overlay);
      markerMesh.userData.highlightOverlay = overlay;
    };

    safeTraverse(bodyObj, (obj) => {
      if (
        !obj?.isMesh ||
        obj.userData?.highlightOverlay ||
        obj.userData?.isHighlightOverlay ||
        obj.userData?.isGlow
      ) {
        return;
      }

      if (obj.userData?.isBodyLabelMarker && !obj.userData?.isBodyLabelMarkerPickProxy) {
        bodyLabelMarker = obj;
      }

      const meshOwnerBodyUuid = obj.userData?.bodyUuid || null;
      if (meshOwnerBodyUuid && String(meshOwnerBodyUuid) !== String(targetBodyUuid)) return;

      let cur = obj;
      while (cur && cur !== bodyObj.parent) {
        const ownerBodyUuid = cur.userData?.bodyUuid || null;
        if (ownerBodyUuid && String(ownerBodyUuid) !== String(targetBodyUuid)) return;

        const type = cur.userData?.type;
        if (type === "mesh" || type === "visual") {
          addHighlightOverlay(obj);
          highlightedVisual = true;
          return;
        }
        cur = cur.parent;
      }
    });

    if (!highlightedVisual && bodyLabelMarker) {
      addBodyLabelMarkerHighlightOverlay(bodyLabelMarker);
    }
  };

  const addHighlightOverlayToVisualGeom = useCallback(
    (bodyUuid, geom) => {
      if (!sceneRef.current || !bodyUuid || !geom?.uuid) return;
      safeTraverse(sceneRef.current, (obj) => {
        if (!obj?.isMesh || obj.userData?.isHighlightOverlay) return;
        let cur = obj;
        while (cur) {
          const ud = cur.userData || {};
          const matchesPrimitiveVisual =
            ud.type === "visual" &&
            String(ud.bodyUuid || "") === String(bodyUuid) &&
            String(ud.geomUuid || "") === String(geom.uuid);
          const matchesMeshVisual =
            obj.userData?.type === "mesh" &&
            String(obj.userData.bodyUuid || "") === String(bodyUuid) &&
            ((obj.userData.geomUuid && String(obj.userData.geomUuid) === String(geom.uuid)) ||
              (!!geom.mesh && String(obj.userData.meshName || "") === String(geom.mesh)));
          if (matchesPrimitiveVisual || matchesMeshVisual) {
            addHighlightOverlay(obj);
            return;
          }
          cur = cur.parent;
        }
      });
    },
    [sceneRef]
  );

  // Handle selection highlights
  useEffect(() => {
    if (!sceneRef.current) return;

    removeJointSelectionHighlights(sceneRef.current);

    // Remove all highlight overlays first
    Object.values(sceneObjects).forEach((obj) => {
      safeTraverse(obj, (child) => {
        if (child) {
          removeHighlightOverlay(child);
        }
      });
    });

    const selectedVisualGeom =
      selectedGeom && bodies ? findGeomNode(bodies, selectedGeom)?.geom || null : null;
    if (selectedVisualGeom?.visualOnly === true && selectionHighlightUi.visualGeom && showVisual) {
      const resolved = findGeomNode(bodies, selectedGeom);
      addHighlightOverlayToVisualGeom(resolved?.bodyUuid || null, resolved?.geom || null);
      return;
    }

    // Highlight mesh if selected (array or string)
    if (
      (highlightedMesh || selectedVisualMeshMultiKey) &&
      selectionHighlightUi.mesh &&
      showVisual
    ) {
      const meshNames = [
        ...(Array.isArray(highlightedMesh)
          ? highlightedMesh
          : highlightedMesh
            ? [highlightedMesh]
            : []),
        ...Array.from(selectedVisualMeshIdsForScene),
      ];
      Object.values(sceneObjects).forEach((obj) => {
        safeTraverse(obj, (child) => {
          const meshId = child?.userData?.meshName;
          if (child && child.isMesh && meshId && meshNames.includes(meshId)) {
            addHighlightOverlay(child);
          }
        });
      });
      return;
    }

    if (selectedJoint && selectionHighlightUi.joint) {
      addJointSelectionHighlight(sceneRef.current, selectedJoint);
      return;
    }

    const multiSelectedBodiesState = useSelectionStore.getState().selectedBodies;
    const multiSelectedBodies =
      multiSelectedBodiesState.length > 1 ? multiSelectedBodiesState : null;
    if (multiSelectedBodies && selectionHighlightUi.body && showVisual) {
      multiSelectedBodies.forEach((bodyUuid) => {
        const bodyObj = sceneObjects[bodyUuid];
        if (bodyObj) addHighlightOverlayToBodyObject(bodyObj, bodyUuid);
      });
      return;
    }

    // Highlight body if selected (single or hover-driven)
    if (
      highlightedBody &&
      selectionHighlightUi.body &&
      showVisual &&
      sceneObjects[highlightedBody]
    ) {
      const bodyObj = sceneObjects[highlightedBody];
      addHighlightOverlayToBodyObject(bodyObj, highlightedBody);
    }
  }, [
    bodies,
    highlightedBody,
    highlightedJoint,
    highlightedMesh,
    sceneObjects,
    selectedGeom,
    selectedJoint,
    selectedBodiesMultiKey,
    selectedVisualMeshIdsForScene,
    selectedVisualMeshMultiKey,
    selectionHighlightUi,
    showVisual,
    addHighlightOverlayToVisualGeom,
    sceneRef,
  ]);

  // Provides hover preview highlights when no explicit selection is active
  useEffect(() => {
    if (!sceneRef.current) return;

    // When anything is explicitly selected (including joints), let the selection effect own overlays.
    // This prevents dual-active visuals where one joint is selected while another is hovered.
    if (
      highlightedBody ||
      highlightedMesh ||
      selectedVisualMeshMultiKey ||
      highlightedJoint ||
      selectedGeom ||
      (selectionHighlightUi.body && selectedBodiesMultiKey)
    ) {
      return;
    }

    removeJointSelectionHighlights(sceneRef.current);

    // Remove all highlight overlays first
    Object.values(sceneObjects).forEach((obj) => {
      safeTraverse(obj, (child) => {
        if (child) {
          removeHighlightOverlay(child);
        }
      });
    });

    // Add highlight for hovered mesh (for uploaded meshes)
    if (hoverPreviewMesh && sceneObjects[hoverPreviewMesh]) {
      const meshObj = sceneObjects[hoverPreviewMesh];
      if (meshObj && meshObj.isMesh) {
        addHighlightOverlay(meshObj);
      }
      return;
    }

    // Add highlight for hovered body/joint
    if (hoverPreviewBody && sceneObjects[hoverPreviewBody]) {
      const bodyObj = sceneObjects[hoverPreviewBody];
      addHighlightOverlayToBodyObject(bodyObj, hoverPreviewBody);
    } else if (hoverPreviewJoint && sceneRef.current) {
      addJointSelectionHighlight(sceneRef.current, hoverPreviewJoint);
    }
  }, [
    hoverPreviewBody,
    hoverPreviewJoint,
    hoverPreviewMesh,
    sceneObjects,
    highlightedBody,
    highlightedMesh,
    selectedVisualMeshMultiKey,
    highlightedJoint,
    selectedGeom,
    selectedBodiesMultiKey,
    selectionHighlightUi.body,
    sceneRef,
  ]);

  // Adjusts material properties for legacy MeshPhongMaterial (shininess only)
  useEffect(() => {
    Object.values(sceneObjects).forEach((obj) => {
      safeTraverse(obj, (child) => {
        if (child.isMesh && child.material && child.material.isMeshPhongMaterial) {
          child.material.shininess = 80;
          child.material.needsUpdate = true;
        }
        // Note: MeshStandardMaterial properties are managed by the material editor
        // and should not be overridden here
      });
    });
  }, [sceneObjects]);
}
